// src/hooks/useAppTheme.js
// One theme for the whole app: <html data-theme="brown|green">, same key and values the landing page uses.
import { useEffect, useState } from "react";

export const THEME_KEY = "pref-theme";

export const THEMES = {
  brown: { label: "Brown", primary: "#b89b6e", dot: "#b89b6e" },
  green: { label: "Green", primary: "#16a34a", dot: "#4f7d5b" },
};

const normalize = (v) => (v === "green" ? "green" : "brown");

const readStored = () => {
  try {
    return normalize(localStorage.getItem(THEME_KEY));
  } catch {
    return "brown";
  }
};

const readDom = () => normalize(document.documentElement.dataset.theme);

export const setAppTheme = (theme) => {
  const next = normalize(theme);
  try {
    localStorage.setItem(THEME_KEY, next);
  } catch {
    /* storage unavailable */
  }
  document.documentElement.dataset.theme = next;
};

// Apply the saved theme as soon as this module loads so the first paint is already themed.
document.documentElement.dataset.theme = readStored();

export default function useAppTheme() {
  const [theme, setTheme] = useState(readDom);

  useEffect(() => {
    // The DOM attribute is the single source of truth (the landing page's own dock also sets it).
    const observer = new MutationObserver(() => setTheme(readDom()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });

    // Another tab changed the theme.
    const onStorage = (e) => {
      if (e.key === THEME_KEY) document.documentElement.dataset.theme = readStored();
    };
    window.addEventListener("storage", onStorage);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return { theme, setTheme: setAppTheme, palette: THEMES[theme] };
}
